import { Project } from '@beeboard/common';
import { plusXDaysString } from '@/utils/helpers/datetime';

interface ForceDeadlineDateLimits {
  minDate?: string;
  maxDate?: string;
}

// Date-based force-update window for a final milestone's deadline pickers —
// the date-space mirror of calculateFinalMilestoneForceDeadlinePositions
// (the drag clamps): only STATE-BLOCKING children bound the range, exactly
// like a force drag on the map. Non-blocking children are crossable (they
// relocate with the boundary); landing exactly ON another milestone's date is
// rejected by the shared validation on save (the drag skips those days
// instead). Blocking activities may share the boundary date; blocking
// intermediate milestones may not.
export function calculateFinalMilestoneForceDeadlineDates(
  project: Project,
  phaseId: string | undefined,
): ForceDeadlineDateLimits {
  const phaseIndex = project.phases.findIndex((p) => p.id === phaseId);
  const phase = project.phases[phaseIndex];
  const nextPhase = project.phases[phaseIndex + 1];
  if (!phase || !nextPhase) {
    return {};
  }

  const isActive = (state: string) =>
    state === 'planned' || state === 'underway';

  // forward: stop before a blocking next-phase child — done ones never
  // relocate, and an unplanned phase must not swallow active children
  const blocksForward = (state: string) =>
    state === 'done' || (phase.state === 'unplanned' && isActive(state));
  const forwardBlocker = [
    ...project.activities
      .filter((a) => a.phase === nextPhase.id && blocksForward(a.state))
      .map((a) => a.current_deadline),
    ...nextPhase.intermediate_milestones
      .filter((m) => blocksForward(m.state))
      .map((m) => m.current_deadline),
  ]
    .sort()
    .at(0);
  const maxDate = forwardBlocker
    ? plusXDaysString(forwardBlocker, -1)
    : undefined;

  // backward: a pushed own child must not land where the rules reject it —
  // done children never relocate, a done next phase admits nothing, and an
  // unplanned next phase admits no planned/underway children
  const blocksBackward = (state: string) =>
    state === 'done' ||
    nextPhase.state === 'done' ||
    (nextPhase.state === 'unplanned' && isActive(state));
  const minDate = [
    ...project.activities
      .filter((a) => a.phase === phaseId && blocksBackward(a.state))
      .map((a) => plusXDaysString(a.current_deadline, 0)),
    ...phase.intermediate_milestones
      .filter((m) => blocksBackward(m.state))
      .map((m) => plusXDaysString(m.current_deadline, 1)),
  ]
    .sort()
    .at(-1);

  return { minDate, maxDate };
}
